// Tracks the user's subscription state by reading two response headers
// the backend's requireActiveSubscription middleware sets on every
// authenticated request:
//   x-trial-days-remaining: integer (can be negative)
//   x-subscription-status:  active | trial | expired | suspended
//
// This module wraps window.fetch once at app boot — so existing API
// callers (which all use plain fetch) don't need to be touched. Every
// successful authenticated response automatically updates the store.
// Components subscribe via the React hook in TrialBanner.

let state = {
  daysRemaining: null,        // number or null
  status: null,               // 'active' | 'trial' | 'expired' | 'suspended' | null
  lastUpdatedAt: null,
};
const listeners = new Set();

function notify() {
  for (const l of listeners) {
    try { l(state); } catch (_) { /* silent — bad listener shouldn't break others */ }
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(state); // emit current
  return () => listeners.delete(fn);
}

export function getState() { return state; }

function maybeUpdate(response) {
  // response.headers may be a Headers instance (fetch) or undefined.
  if (!response || !response.headers || typeof response.headers.get !== 'function') return;
  const status = response.headers.get('x-subscription-status');
  const daysStr = response.headers.get('x-trial-days-remaining');
  // Skip frames that don't carry the headers (public routes, error
  // pages, etc.) — we only want to flip state when the backend
  // actually sent fresh info.
  if (!status && daysStr == null) return;
  const days = daysStr == null ? state.daysRemaining : Number(daysStr);
  const next = {
    daysRemaining: Number.isFinite(days) ? days : null,
    status: status || state.status,
    lastUpdatedAt: Date.now(),
  };
  // Only notify on actual change so React re-renders are minimised.
  if (next.daysRemaining !== state.daysRemaining || next.status !== state.status) {
    state = next;
    notify();
  } else {
    state = { ...state, lastUpdatedAt: next.lastUpdatedAt };
  }
}

// Install once at app boot. Subsequent calls are no-ops.
let installed = false;
export function installSubscriptionInterceptor() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await original(...args);
    try { maybeUpdate(res); } catch (_) { /* never break the request */ }
    return res;
  };
}
